import axios from "axios";
import { API_BASE_URL } from "@/lib/apiBase";

function authHeaders(token) {
  return { Authorization: `Bearer ${token}` };
}

export async function listSocialMutualFollows(token, q = "", opts = {}) {
  const res = await axios.get(`${API_BASE_URL}/social/dm/mutual-follows`, {
    headers: authHeaders(token),
    params: { q: q || undefined, limit: opts.limit },
  });
  return res.data?.users || [];
}

export async function listSocialDmConversations(token, opts = {}) {
  const res = await axios.get(`${API_BASE_URL}/social/dm/conversations`, {
    headers: authHeaders(token),
    params: {
      unread_only: opts.unreadOnly ? "1" : undefined,
    },
  });
  return res.data || [];
}

export async function getSocialDmUnreadCount(token) {
  const res = await axios.get(`${API_BASE_URL}/social/dm/unread-count`, {
    headers: authHeaders(token),
  });
  return Number(res.data?.unread_count) || 0;
}

export async function getOrCreateSocialDm(token, peerUserId) {
  const res = await axios.post(
    `${API_BASE_URL}/social/dm/conversations`,
    { peer_user_id: peerUserId },
    { headers: authHeaders(token) }
  );
  return res.data;
}

export async function createSocialDmGroup(token, { title, memberUserIds }) {
  const body = { member_user_ids: memberUserIds };
  if (title != null && String(title).trim()) {
    body.title = String(title).trim();
  }
  const res = await axios.post(
    `${API_BASE_URL}/social/dm/conversations/groups`,
    body,
    { headers: authHeaders(token) }
  );
  return res.data;
}

export async function getSocialDmConversation(token, conversationId) {
  const res = await axios.get(
    `${API_BASE_URL}/social/dm/conversations/${conversationId}`,
    { headers: authHeaders(token) }
  );
  return res.data;
}

export async function updateSocialDmGroup(token, conversationId, { title }) {
  const res = await axios.patch(
    `${API_BASE_URL}/social/dm/conversations/${conversationId}`,
    { title },
    { headers: authHeaders(token) }
  );
  return res.data;
}

export async function listSocialDmMembers(token, conversationId) {
  const res = await axios.get(
    `${API_BASE_URL}/social/dm/conversations/${conversationId}/members`,
    { headers: authHeaders(token) }
  );
  return res.data?.members || [];
}

export async function addSocialDmMembers(token, conversationId, userIds) {
  const res = await axios.post(
    `${API_BASE_URL}/social/dm/conversations/${conversationId}/members`,
    { user_ids: userIds },
    { headers: authHeaders(token) }
  );
  return res.data;
}

export async function removeSocialDmMember(token, conversationId, targetUserId) {
  const res = await axios.delete(
    `${API_BASE_URL}/social/dm/conversations/${conversationId}/members/${encodeURIComponent(
      targetUserId
    )}`,
    { headers: authHeaders(token) }
  );
  return res.data;
}

export async function listSocialDmMentionables(token, conversationId, q = "") {
  const res = await axios.get(
    `${API_BASE_URL}/social/dm/conversations/${conversationId}/mentionables`,
    {
      headers: authHeaders(token),
      params: { q: q || undefined },
    }
  );
  return res.data?.users || [];
}

export async function listSocialDmMessages(token, conversationId, opts = {}) {
  const res = await axios.get(
    `${API_BASE_URL}/social/dm/conversations/${conversationId}/messages`,
    {
      headers: authHeaders(token),
      params: {
        limit: opts.limit,
        before: opts.beforeId,
      },
    }
  );
  return {
    messages: Array.isArray(res.data?.messages) ? res.data.messages : [],
    peer_last_read_at: res.data?.peer_last_read_at ?? null,
  };
}

export async function sendSocialDmMessage(token, conversationId, body) {
  const res = await axios.post(
    `${API_BASE_URL}/social/dm/conversations/${conversationId}/messages`,
    body,
    { headers: authHeaders(token) }
  );
  return res.data;
}

export async function markSocialDmRead(token, conversationId) {
  const res = await axios.post(
    `${API_BASE_URL}/social/dm/conversations/${conversationId}/read`,
    {},
    { headers: authHeaders(token) }
  );
  return res.data;
}

export function socialDmDraftStorageKey(conversationId) {
  return `social-dm-draft:${conversationId}`;
}

export function loadSocialDmDraft(conversationId) {
  try {
    return (
      localStorage.getItem(socialDmDraftStorageKey(conversationId)) || ""
    );
  } catch {
    return "";
  }
}

export function saveSocialDmDraft(conversationId, text) {
  try {
    const key = socialDmDraftStorageKey(conversationId);
    if (!text) localStorage.removeItem(key);
    else localStorage.setItem(key, text);
  } catch {
    /* ignore */
  }
}

export function clearSocialDmDraft(conversationId) {
  try {
    localStorage.removeItem(socialDmDraftStorageKey(conversationId));
  } catch {
    /* ignore */
  }
}
